import axios from 'axios';
import config from '../config';

// Helper function to get CSRF token from cookie
const getCSRFToken = () => {
  const cookies = document.cookie.split(';');
  for (let cookie of cookies) {
    const [name, value] = cookie.trim().split('=');
    if (name === 'csrf' || name === '__Host-csrf') {
      return value;
    }
  }
  return null;
};

// Create axios instance with default config and extended timeout for long-running operations
const api = axios.create({
  baseURL: config.API_BASE_URL,
  timeout: config.API_TIMEOUT,
  headers: {
    'Content-Type': 'application/json'
  },
  withCredentials: true // Important for cookies
});

// Create a separate instance for quick operations with shorter timeout
const quickApi = axios.create({
  baseURL: config.API_BASE_URL,
  timeout: config.QUICK_API_TIMEOUT,
  headers: {
    'Content-Type': 'application/json'
  },
  withCredentials: true // Important for cookies
});

// Add request interceptor to include CSRF token for both instances
const addAuthInterceptor = (apiInstance) => {
  apiInstance.interceptors.request.use(
    (config) => {
      // Always attach CSRF header if present (backend requires it even for GET)
      const csrfToken = getCSRFToken();
      if (csrfToken) {
        config.headers['X-CSRF-Token'] = csrfToken;
      }


      return config;
    },
    (error) => {
      return Promise.reject(error);
    }
  );
};

// Add response interceptor to handle errors gracefully
const addResponseInterceptor = (apiInstance) => {
  apiInstance.interceptors.response.use(
    (response) => response,
    async (error) => {
      const originalRequest = error.config;

      // Handle timeout errors gracefully
      if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
        // Return a graceful timeout response instead of throwing error
        return {
          data: {
            success: false,
            message: 'Обработка занимает больше времени чем обычно. Анализ продолжается в фоновом режиме.',
            data: null,
            timeout: true,
            processing: true
          }
        };
      }

      // Handle 401 authentication errors
      if (error.response?.status === 401 && !originalRequest._retry) {
        originalRequest._retry = true;

        try {
          // Try to refresh token using cookies
          const csrfToken = getCSRFToken();
          const headers = {};
          if (csrfToken) {
            headers['X-CSRF-Token'] = csrfToken;
          }

          await axios.post('/api/v1/auth/refresh', {}, {
            headers,
            withCredentials: true
          });

          // Cookies are updated automatically by the backend
          // Retry the original request
          return apiInstance(originalRequest);
        } catch (refreshError) {
          // Refresh failed - session expired
          console.warn('[API] Token refresh failed, session expired:', refreshError);
          
          // Dispatch event to notify AuthContext
          window.dispatchEvent(new CustomEvent('authError'));
          
          return Promise.reject(refreshError);
        }
      }

      // Handle other HTTP errors gracefully
      if (error.response?.status >= 500) {
        return {
          data: {
            success: false,
            message: 'Временная проблема с сервером. Попробуйте позже.',
            data: null,
            serverError: true
          }
        };
      }

      return Promise.reject(error);
    }
  );
};

// Apply interceptors to both instances
addAuthInterceptor(api);
addAuthInterceptor(quickApi);
addResponseInterceptor(api);
addResponseInterceptor(quickApi);

// Submit a new analysis task
export const submitAnalysisTask = async (city, minOrganizations) => {
  try {
    const response = await api.post('/tasks/analyze', {
      city,
      min_organizations: minOrganizations
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

// NEW: Search companies by city with async worker pool
export const searchCompaniesByCity = async (city, limit = 50, category = null, withAnalysis = true) => {
  try {
    const params = new URLSearchParams({
      limit: limit.toString(),
      with_analysis: withAnalysis.toString()
    });
    if (category) {
      params.append('category', category);
    }

    const response = await api.get(`/companies/search-city/${encodeURIComponent(city)}?${params}`);
    return response.data;
  } catch (error) {
    throw error;
  }
};

// NEW: Check worker pool health
export const checkWorkerPoolHealth = async () => {
  try {
    const response = await api.get('/companies/worker-pool/health');
    return response.data;
  } catch (error) {
    throw error;
  }
};

// NEW: Get company analysis progress (for WebSocket or polling)
export const getAnalysisProgress = async (sessionId) => {
  try {
    const response = await api.get(`/companies/analysis-progress/${sessionId}`);
    return response.data;
  } catch (error) {
    throw error;
  }
};

// NEW: Check for completed analysis results in MinIO storage
export const checkCompletedResults = async (city) => {
  try {
    const response = await quickApi.get(`/companies/check-results/${encodeURIComponent(city)}`);
    return response.data;
  } catch (error) {
    // 404 means no results found yet - this is expected during polling
    if (error.response?.status === 404) {
      return {
        noResults: true,
        message: 'Анализ еще не завершен'
      };
    }
    throw error;
  }
};

// Get task status
export const getTaskStatus = async (taskId) => {
  try {
    const response = await api.get(`/tasks/${taskId}/status`);
    return response.data;
  } catch (error) {
    throw error;
  }
};

// Cancel a running task
export const cancelTask = async (taskId) => {
  try {
    const response = await api.post(`/tasks/${taskId}/cancel`);
    return response.data;
  } catch (error) {
    throw error;
  }
};

// Get task result
export const getTaskResult = async (taskId) => {
  try {
    const response = await api.get(`/tasks/${taskId}/result`);
    return response.data;
  } catch (error) {
    // 404 означает что результат ещё не готов — это нормально во время поллинга
    if (error.response?.status === 404) {
      return null;
    }
    throw error;
  }
};

// Check worker pool health
export const getWorkerPoolHealth = async () => {
  try {
    const response = await api.get('/companies/worker-pool/health');
    return response.data;
  } catch (error) {
    throw error;
  }
};

// Get cached cities
export const getCachedCities = async () => {
  try {
    const response = await api.get('/cached-cities');
    return response.data;
  } catch (error) {
    throw error;
  }
};

// NEW: Search cities for autocomplete
export const searchCities = async (query) => {
  try {
    const response = await api.get(`/cities/search`, {
      params: { query, limit: 10 }
    });
    return response.data;
  } catch (error) {
    console.error('Error searching cities:', error);
    return { items: [] };
  }
};

// NEW: Get search history from MinIO storage
export const getSearchHistory = async () => {
  try {
    const response = await api.get('/companies/search-history');
    return response.data;
  } catch (error) {
    throw error;
  }
};

// NEW: Get search results by task ID from MinIO storage
export const getSearchResultsByTaskId = async (taskId) => {
  try {
    const response = await api.get(`/companies/search-history/${taskId}`);
    return response.data;
  } catch (error) {
    throw error;
  }
};

// ==================== Company Status API ====================

// Helper to get company identifier (inn or internal_id)
export const getCompanyIdentifier = (company) => {
  // Prefer INN if available and valid (10 or 12 digits)
  if (company?.inn) {
    const digits = String(company.inn).replace(/\s/g, '');
    if (/^\d{10}$|^\d{12}$/.test(digits)) {
      return { type: 'inn', value: digits };
    }
  }
  // Fall back to internal_id
  if (company?.internal_id) {
    return { type: 'internal_id', value: company.internal_id };
  }
  return null;
};

// Get company status for current user (by inn or internal_id)
export const getCompanyStatus = async (identifier) => {
  if (!identifier) return null;
  try {
    const response = await quickApi.get(`/companies/status/${encodeURIComponent(identifier)}`);
    return response.data;
  } catch (error) {
    // 404 means no status set yet - return null
    if (error.response?.status === 404) {
      return null;
    }
    throw error;
  }
};

// Set company status and comment (by inn or internal_id)
export const setCompanyStatus = async (identifier, status, comment = '') => {
  if (!identifier) throw new Error('Company identifier is required');
  try {
    const response = await quickApi.post(`/companies/status/${encodeURIComponent(identifier)}`, {
      status,
      comment: comment || ''
    });
    return response.data;
  } catch (error) {
    console.error('setCompanyStatus error:', error.response?.data || error.message);
    throw error;
  }
};

// Delete company status (make it "new" again)
export const deleteCompanyStatus = async (identifier) => {
  if (!identifier) return { success: true };
  try {
    await quickApi.delete(`/companies/status/${encodeURIComponent(identifier)}`);
    return { success: true };
  } catch (error) {
    // 404 means status didn't exist - that's ok
    if (error.response?.status === 404) {
      return { success: true };
    }
    console.error('deleteCompanyStatus error:', error.response?.data || error.message);
    throw error;
  }
};

// Get statuses for multiple companies (batch)
// Accepts array of { identifier, company } objects
export const getCompanyStatusesBatch = async (companies) => {
  try {
    // Build list of identifiers
    const identifiers = companies
      .map(company => {
        const id = getCompanyIdentifier(company);
        return id ? { ...id, company } : null;
      })
      .filter(Boolean);

    if (identifiers.length === 0) return {};

    // Fetch statuses in parallel
    const results = await Promise.allSettled(
      identifiers.map(({ value }) => getCompanyStatus(value))
    );
    
    // Build map identifier -> status
    const statusMap = {};
    results.forEach((result, index) => {
      if (result.status === 'fulfilled' && result.value) {
        const { value } = identifiers[index];
        statusMap[value] = result.value;
      }
    });
    
    return statusMap;
  } catch (error) {
    console.error('Error fetching batch statuses:', error);
    return {};
  }
};

// ==================== Managers API ====================

// Get managers list with optional filtering
export const getManagers = async (search = '', baseCode = '') => {
  try {
    const params = new URLSearchParams();
    if (search) {
      params.append('search', search);
    }
    if (baseCode) {
      params.append('base_code', baseCode);
    }
    const response = await quickApi.get(`/managers?${params}`);
    return response.data;
  } catch (error) {
    throw error;
  }
};

// NEW: Export companies to 1C by task ID with manager
export const exportCompaniesTo1C = async (taskId, managerId, companyInn = null) => {
  try {
    // Формируем URL с параметрами
    let url = `/export/to-1c?task_id=${taskId}`;
    if (companyInn) {
      url += `&company_inn=${encodeURIComponent(companyInn)}`;
    }

    const response = await api.post(url, {
      manager_id: managerId
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

export default api;
